import { useEffect, useState } from 'react'
import {
  useAkuteClinicians,
  useAkuteNotePreview,
} from '@pelvia/shared/hooks/patient/useAkute'

/**
 * Preview-then-send modal for the PelviaCare+ consult note.
 *
 * Two modes over one panel:
 *  - `send` — shows the note preview the send will push **and** the clinician
 *    picker (the patient chooses who authors + signs). Parent owns the send.
 *  - `preview` — read-only: shows the note a clinician is already reviewing
 *    (note already pushed), with no picker and no send action.
 *
 * The preview content comes from `GET /api/akute/note/preview` — the exact
 * sectioned note the send builds from the patient's tracked data.
 */
export default function SendNoteModal({
  mode = 'send',
  selectedId,
  sending,
  onClose,
  onConfirm,
}: {
  mode?: 'send' | 'preview'
  selectedId?: string | null
  sending: boolean
  onClose: () => void
  onConfirm: (clinicianId: string) => void
}) {
  const isPreview = mode === 'preview'
  const preview = useAkuteNotePreview()
  // Only the send flow needs the picker list.
  const { data, isLoading, isError } = useAkuteClinicians(!isPreview)
  const clinicians = data?.clinicians ?? []
  const [chosen, setChosen] = useState<string>('')

  // Default to the previously-chosen clinician, else the first (prescribers come
  // first from the API). Runs once the list arrives.
  useEffect(() => {
    if (isPreview || chosen || clinicians.length === 0) return
    const saved = selectedId && clinicians.some((c) => c.id === selectedId)
    setChosen(saved ? (selectedId as string) : clinicians[0].id)
  }, [clinicians, selectedId, chosen, isPreview])

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel send-note-panel" onClick={(e) => e.stopPropagation()}>
        <h3>{isPreview ? 'Your consult note' : 'Review & send your note'}</h3>
        <p>
          {isPreview
            ? 'A preview of your consult note, built from your latest tracked data. Your clinician is reviewing the version sent for sign-off.'
            : "Here's the consult note we'll send, built from your tracked data. Choose the clinician who will review and sign it."}
        </p>

        <NotePreview
          loading={preview.isLoading}
          error={preview.isError}
          sections={preview.data?.sections}
          serviceDate={preview.data?.service_date}
        />

        {!isPreview && (
          <>
            <p className="send-note-picker-label">Reviewing &amp; signing clinician</p>
            {isLoading && <p>Loading clinicians…</p>}
            {isError && (
              <p className="integration-error">
                Couldn't load clinicians right now. Please try again.
              </p>
            )}
            {!isLoading && !isError && clinicians.length === 0 && (
              <p className="integration-error">
                No clinicians are available on your care team yet.
              </p>
            )}
            {clinicians.length > 0 && (
              <ul className="clinician-list">
                {clinicians.map((c) => (
                  <li key={c.id}>
                    <label className="clinician-option">
                      <input
                        type="radio"
                        name="clinician"
                        value={c.id}
                        checked={chosen === c.id}
                        onChange={() => setChosen(c.id)}
                      />
                      <span className="clinician-name">{c.name}</span>
                      {c.is_prescriber && (
                        <span className="clinician-badge">Prescriber</span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={sending}>
            {isPreview ? 'Close' : 'Cancel'}
          </button>
          {!isPreview && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => chosen && onConfirm(chosen)}
              disabled={!chosen || sending}
            >
              {sending ? 'Sending…' : 'Send my note'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function NotePreview({
  loading,
  error,
  sections,
  serviceDate,
}: {
  loading: boolean
  error: boolean
  sections?: { title: string; text: string }[]
  serviceDate?: string
}) {
  if (loading) return <p className="note-preview-empty">Building your note preview…</p>
  if (error || !sections) {
    return <p className="note-preview-empty">Couldn't load the note preview right now.</p>
  }
  return (
    <div className="note-preview">
      {serviceDate && <p className="note-preview-date">Date of service: {serviceDate}</p>}
      {sections.map((s) => (
        <details key={s.title} className="note-preview-section">
          <summary>{s.title}</summary>
          <pre className="note-preview-text">{s.text || '—'}</pre>
        </details>
      ))}
    </div>
  )
}
